import { SignJWT } from 'jose';

import { env } from '$env/dynamic/public';
import { getCookie } from '$lib/helpers/session';
import { PUBLIC_API_URL } from '$env/static/public';
import type { RpcRequestMap, RpcResponseMap } from './flow-rpc.types';

export type TokenName = 'prospect_token' | 'token' | 'client_auth';
type ValidEndpoint = keyof RpcResponseMap & keyof RpcRequestMap;
interface RpcConfig<E extends ValidEndpoint> {
  body: RpcRequestMap[E];
  endpoint: E;
  fetchFn?: typeof fetch;
  // array of strings for times that we may need to try multiple tokens
  // e.g. a GP visiting a data room ("token") vs a prospect ("prospect_token")
  tokenNames: TokenName[];
}
export async function fetchRpc<E extends keyof RpcResponseMap>({
  body,
  endpoint,
  // when invoking fetchRpc on +page.ts or +layout.ts
  // prefer to use the fetch provided as an arg by SvelteKit's load.
  // Otherwise, default to window fetch
  fetchFn = fetch,
  tokenNames,
}: RpcConfig<E>): Promise<RpcResponseMap[E]> {
  const headers = new Headers({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });

  for (const tokenName of tokenNames) {
    const token = getCookie(tokenName);

    if (tokenName === 'client_auth') {
      // --- CLIENT SIDE JWT GENERATION ---
      const secret = new TextEncoder().encode(env.PUBLIC_RPC_CLIENT_AUTH_SIGNATURE);

      const clientToken = await new SignJWT()
        .setProtectedHeader({ alg: 'HS256' })
        .setIssuedAt()
        .setExpirationTime('5m')
        .sign(secret);

      headers.set('Authorization', `Bearer ${clientToken}`);
      break;
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      break;
    }
  }

  const impersonationCookie = getCookie('impersonation_token');

  if (impersonationCookie) {
    const token = impersonationCookie.replace('impersonation_token=', '');
    headers.set('Impersonation-Token', token);
  }

  const res = await fetchFn(`${PUBLIC_API_URL}/rpc/${endpoint}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    // throw to caller. In places where we are invoking fetchRpc on a component
    // use a try-catch and add a failure toast that receives this response
    // see getErrorMessage helper
    throw res;
  }

  // Parse as JSON only if there is text, otherwise return null/undefined
  // This helps handle empty response bodies
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as RpcResponseMap[E];
}
